import { useMemo, useCallback, useState, useEffect } from "react";
import { motion, LayoutGroup } from "motion/react";
import { Link } from "@tanstack/react-router";
import {
  useFetchClubsQuery,
  useFetchAllClubMembersQuery,
  useToggleClubPinMutation,
  useGetFeaturedClubsQuery,
  useGetRecommendedClubsQuery,
  useFetchCurrentUserProfileQuery,
  useUpdateUserProfileMutation,
  type Club,
  type ClubMember,
  type FeaturedClub,
  type RecommendedClub,
} from "@/api/clubsApi";
import { useAuth } from "@/components/providers/AuthProvider";
import { useClubVisitHistory } from "@/hooks/useClubVisitHistory";
import { ClubCard } from "./ClubCard";
import { CreateClubCard } from "./CreateClubCard";
import { FeaturedClubCard } from "./FeaturedClubCard";
import { Icon } from "@/components/ui/Icon";

// Module-level cache — survives component unmount/remount without re-fetching geolocation
let cachedCoords: { latitude: number; longitude: number } | null = null;

export function ClubsList() {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { sortClubsByRecency } = useClubVisitHistory();

  // Geolocation for featured clubs (best-effort — global featured clubs show even without location)
  const [userCoords, setUserCoords] = useState<{ latitude: number; longitude: number }>(
    cachedCoords || { latitude: 0, longitude: 0 }
  );
  useEffect(() => {
    if (cachedCoords) return; // Already have location from a previous mount
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
          cachedCoords = coords;
          setUserCoords(coords);
        },
        () => {} // Keep defaults (0,0) — global featured clubs still return
      );
    }
  }, []);

  // Current user profile for dismissed recommendations
  const { data: currentProfile } = useFetchCurrentUserProfileQuery(currentUserId || "", {
    skip: !currentUserId,
  });
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [dismissedClubIds, setDismissedClubIds] = useState<string[]>([]);

  // Fetch all clubs and all club members (same pattern as mobile)
  const { data: clubsData, isLoading: clubsLoading, error: clubsError } = useFetchClubsQuery();
  const { data: clubMembersData, isLoading: membersLoading } = useFetchAllClubMembersQuery();

  // Fetch featured clubs — runs immediately with 0,0 coords (global clubs show regardless of location);
  // re-fetches with real coords once geolocation resolves
  const { data: featuredClubs } = useGetFeaturedClubsQuery(
    {
      latitude: userCoords.latitude,
      longitude: userCoords.longitude,
      userId: currentUserId || "",
      limit: null,
    },
    { skip: !currentUserId }
  );

  // Fetch recommended (nearby) clubs — same RPC as mobile (get_random_clubs_within_radius)
  const hasRealCoords = userCoords.latitude !== 0 || userCoords.longitude !== 0;
  const { data: recommendedClubs } = useGetRecommendedClubsQuery(
    {
      latitude: userCoords.latitude,
      longitude: userCoords.longitude,
      radiusMiles: 50,
      limit: 5,
      userId: currentUserId || "",
    },
    { skip: !currentUserId || !hasRealCoords }
  );

  // Combine featured + recommended into one discovery list, filtering dismissed (matches mobile)
  const discoveryClubs = useMemo(() => {
    const allDismissed = new Set([
      ...(currentProfile?.dismissed_club_recommendations || []),
      ...dismissedClubIds,
    ]);

    const featured = (featuredClubs || [])
      .filter((c) => !allDismissed.has(c.club_id))
      .map((c) => ({ ...c, is_featured: true as const, distance_miles: null as number | null }));
    const nearby = (recommendedClubs || [])
      .filter((c) => !allDismissed.has(c.club_id))
      .map((c) => ({ ...c, is_featured: false as const }));

    const result: (typeof featured[number] | typeof nearby[number])[] = [];
    let fi = 0;
    let ni = 0;
    while (fi < featured.length || ni < nearby.length) {
      if (fi < featured.length) result.push(featured[fi++]);
      if (ni < nearby.length) result.push(nearby[ni++]);
    }
    return result;
  }, [featuredClubs, recommendedClubs, currentProfile?.dismissed_club_recommendations, dismissedClubIds]);

  // Dismiss a recommendation (matches mobile handleDismissRecommendation)
  const handleDismissRecommendation = useCallback(
    async (clubId: string) => {
      if (!currentProfile || !currentUserId) return;

      // Optimistically hide
      setDismissedClubIds((prev) => [...prev, clubId]);

      // Persist to database
      try {
        const currentDismissed = currentProfile.dismissed_club_recommendations || [];
        await updateUserProfile({
          id: currentUserId,
          dismissed_club_recommendations: [...currentDismissed, clubId],
        }).unwrap();
      } catch (error) {
        console.error("Failed to dismiss club recommendation:", error);
        setDismissedClubIds((prev) => prev.filter((id) => id !== clubId));
      }
    },
    [currentProfile, currentUserId, updateUserProfile]
  );

  // Mutation for toggling pin status
  const [togglePin] = useToggleClubPinMutation();

  // Create a map of club_id -> membership data for quick lookup
  const membershipMap = useMemo(() => {
    const map = new Map<string, ClubMember>();
    clubMembersData?.forEach((cm) => {
      if (cm.user_id === currentUserId && cm.club_id) {
        map.set(cm.club_id, cm);
      }
    });
    return map;
  }, [clubMembersData, currentUserId]);

  // Filter clubs that the current user belongs to (same logic as mobile app line 672-677)
  const userClubs = useMemo(
    () =>
      clubsData?.filter((club) => club.id && membershipMap.has(club.id)) || [],
    [clubsData, membershipMap]
  );

  // Sort clubs: pinned first (by pinned_at ascending), then unpinned by visit recency
  const sortedClubs = useMemo(() => {
    const pinnedClubs: Club[] = [];
    const unpinnedClubs: Club[] = [];

    userClubs.forEach((club) => {
      const membership = club.id ? membershipMap.get(club.id) : null;
      if (membership?.pinned_at) {
        pinnedClubs.push(club);
      } else {
        unpinnedClubs.push(club);
      }
    });

    // Sort pinned clubs by pinned_at ascending (earliest pinned first)
    pinnedClubs.sort((a, b) => {
      const aPin = a.id ? membershipMap.get(a.id)?.pinned_at : null;
      const bPin = b.id ? membershipMap.get(b.id)?.pinned_at : null;
      if (!aPin || !bPin) return 0;
      return new Date(aPin).getTime() - new Date(bPin).getTime();
    });

    // Sort unpinned clubs by visit recency
    const sortedUnpinned = sortClubsByRecency(unpinnedClubs);

    return [...pinnedClubs, ...sortedUnpinned];
  }, [userClubs, membershipMap, sortClubsByRecency]);

  // Check if a club is pinned
  const isClubPinned = useCallback(
    (clubId: string) => {
      const membership = membershipMap.get(clubId);
      return membership?.pinned_at !== null && membership?.pinned_at !== undefined;
    },
    [membershipMap]
  );

  // Handle pin toggle
  const handleTogglePin = useCallback(
    (clubId: string) => {
      if (currentUserId) {
        togglePin({ clubId, userId: currentUserId });
      }
    },
    [currentUserId, togglePin]
  );

  // Profile completion prompt — show for all users who haven't dismissed (matches mobile)
  const showProfilePrompt =
    currentProfile && !currentProfile.onboarding?.profileCompletionPromptDismissed;

  const handleDismissProfilePrompt = useCallback(async () => {
    if (!currentProfile || !currentUserId) return;
    try {
      await updateUserProfile({
        id: currentUserId,
        onboarding: {
          ...currentProfile.onboarding,
          profileCompletionPromptDismissed: true,
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to dismiss profile prompt:", error);
    }
  }, [currentProfile, currentUserId, updateUserProfile]);

  const isLoading = clubsLoading || membersLoading;

  if (isLoading) {
    return <ClubsListSkeleton />;
  }

  if (clubsError) {
    const errorMessage =
      clubsError && typeof clubsError === "object" && "error" in clubsError
        ? String((clubsError as { error: string }).error)
        : "An error occurred";
    return (
      <div className="bg-surface-1 rounded-xl border border-border-tertiary p-8 text-center">
        <p className="text-text-secondary">
          Unable to load your clubs. Please try again.
        </p>
        <p className="text-text-tertiary text-sm mt-2">{errorMessage}</p>
      </div>
    );
  }

  if (sortedClubs.length === 0) {
    return <ClubsEmptyState creatorId={currentUserId} />;
  }

  return (
    <LayoutGroup>
      <div
        className="grid gap-6 w-full justify-center"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 300px))" }}
      >
        {/* Profile completion prompt */}
        {showProfilePrompt && (
          <div className="col-[1/-1] mb-2">
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-surface border border-border-tertiary">
              <div className="w-10 h-10 rounded-xl bg-olive flex items-center justify-center flex-shrink-0">
                <Icon name="user" size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <Link to="/profile" className="text-body-2-medium text-text-primary hover:underline">
                  Complete your profile
                </Link>
                <p className="text-body-3 text-text-secondary mt-0.5">
                  Share a bit more about yourself — it makes connecting easier.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissProfilePrompt}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-1 transition-colors flex-shrink-0"
              >
                <Icon name="xmark" size={14} className="text-text-tertiary" />
              </button>
            </div>
          </div>
        )}

        {/* Discovery section — spans full grid width, horizontal scroll */}
        {discoveryClubs.length > 0 && (
          <div className="col-[1/-1] mb-2">
            <h2 className="text-body-1-medium text-text-primary mb-2">Recommended for you</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              {discoveryClubs.map((club) => (
                <FeaturedClubCard
                  key={club.club_id}
                  club={club}
                  onDismiss={handleDismissRecommendation}
                />
              ))}
            </div>
          </div>
        )}

        {/* User's clubs */}
        {sortedClubs.map((club) => (
          <motion.div
            key={club.id}
            layout
            layoutId={club.id}
            initial={false}
            transition={{
              layout: {
                type: "spring",
                stiffness: 350,
                damping: 30,
              },
            }}
          >
            <ClubCard
              club={club}
              isPinned={club.id ? isClubPinned(club.id) : false}
              onTogglePin={handleTogglePin}
            />
          </motion.div>
        ))}
        {/* Create club card at the end */}
        {currentUserId && (
          <motion.div layout layoutId="create-club-card" initial={false}>
            <CreateClubCard creatorId={currentUserId} />
          </motion.div>
        )}
      </div>
    </LayoutGroup>
  );
}

function ClubsListSkeleton() {
  return (
    <div
      className="grid gap-6 w-full justify-center"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 300px))" }}
    >
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="bg-background rounded-3xl border border-border-tertiary p-2 pb-4 max-w-[300px] w-full animate-pulse"
        >
          {/* Image skeleton - square */}
          <div className="aspect-square bg-surface-2 rounded-2xl" />

          {/* Content skeleton */}
          <div className="pt-4 px-2">
            <div className="h-5 bg-surface-2 rounded w-3/4" />
            <div className="flex items-center justify-between mt-1">
              <div className="h-4 bg-surface-2 rounded w-24" />
              <div className="h-6 w-6 bg-surface-2 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClubsEmptyState({ creatorId }: { creatorId?: string }) {
  return (
    <div
      className="grid gap-6 w-full justify-center"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 300px))" }}
    >
      {/* Show create club card even when empty */}
      {creatorId && <CreateClubCard creatorId={creatorId} />}

      {/* Empty state message */}
      <div className="col-span-full bg-background rounded-3xl border border-border-tertiary p-8 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-surface-2 flex items-center justify-center">
          <Icon name="user-group" size={32} className="text-text-tertiary" />
        </div>
        <h3 className="text-body-1-medium text-text-primary">No clubs yet</h3>
        <p className="text-body-2 text-text-secondary mt-1">
          You are not a member of any clubs.
        </p>
        <p className="text-body-3 text-text-tertiary mt-2">
          Join a club using an invite link or create your own!
        </p>
      </div>
    </div>
  );
}
